/** The three paths that arrive from an email.
 *
 * All of them are reachable signed out — a confirmation link opened in the
 * phone's mail browser has no session, and bouncing someone holding a valid
 * token to a sign-in wall is the single most annoying thing this flow can do.
 *
 * The query string is read at parse time rather than stored, so the token never
 * outlives the page. `/verify` and `/reset` strip it from the address bar once
 * used (see the screens) — a live reset token sitting in browser history, and
 * in whatever syncs that history, is a credential leak.
 */

export type LinkRoute =
  | { kind: 'verify'; token: string | null }
  | { kind: 'reset'; token: string | null }
  | { kind: 'join'; code: string | null }

const PATHS: Record<string, LinkRoute['kind']> = {
  '/verify': 'verify',
  '/reset': 'reset',
  '/join': 'join',
}

/** Null when the path is not one of the email landings. */
export function parseLinkPath(pathname: string, search: string): LinkRoute | null {
  const kind = PATHS[pathname.replace(/\/+$/, '') || '/']
  if (!kind) return null

  const params = new URLSearchParams(search)
  if (kind === 'join') return { kind, code: params.get('code') }
  return { kind, token: params.get('token') }
}

/** Drop the token from the URL without adding a history entry, so it is not
 * left in the address bar, in history, or in whatever syncs either. */
export function stripQuery(): void {
  window.history.replaceState({}, '', window.location.pathname)
}

/** Where an invite code waits while someone signs in to accept it. Session
 * storage, not local: an abandoned invite should not haunt the next visit. */
const PENDING_INVITE = 'nudge.pendingInvite'

export function stashInvite(code: string): void {
  sessionStorage.setItem(PENDING_INVITE, code)
}

export function takeInvite(): string | null {
  const code = sessionStorage.getItem(PENDING_INVITE)
  if (code) sessionStorage.removeItem(PENDING_INVITE)
  return code
}
